<script setup lang="ts">
import { computed } from 'vue'
import type { InvitesType } from '@/modules/expert/events/schemas/schemas'

const props = defineProps<{
  invite: InvitesType
  showCheckbox?: boolean
  selected?: boolean
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:selected', val: boolean): void
  (e: 'click'): void
}>()

const isDisabled = computed(() => !!props.disabled)

function toggleSelected() {
  if (isDisabled.value) return
  emit('update:selected', !props.selected)
}

/**
 * Seleciona ao clicar no card quando estiver no modo bulk,
 * mas não intercepta cliques em controles interativos.
 */
function onCardClick(e: MouseEvent) {
  emit('click')

  if (!props.showCheckbox) return
  if (isDisabled.value) return

  const target = e.target
  if (!(target instanceof HTMLElement)) {
    toggleSelected()
    return
  }

  // evita selecionar quando clicar em inputs/botoes/links/controles do Vuetify
  const interactive = target.closest(
    'button, a, input, textarea, select, label, [role="button"], .v-input, .v-field, .v-select, .v-checkbox'
  )
  if (interactive) return

  toggleSelected()
}
</script>

<template>
  <div
    class="ric-wrapper d-flex w-100"
    :class="{ 'ric-wrapper--selected': !!selected }"
  >
    <div v-if="showCheckbox" class="ric-checkbox d-flex align-center">
      <v-checkbox
        :model-value="!!selected"
        :disabled="isDisabled"
        hide-details
        density="compact"
        class="ma-0 pa-0"
        @update:model-value="toggleSelected"
        @click.stop
      />
    </div>

    <v-card
      class="ric-card w-100 rounded-lg"
      variant="flat"
      :disabled="isDisabled"
      @click="onCardClick"
    >
      <v-card-text class="ric-content">
        <!-- Header (nome + chevron/menu) -->
        <div class="ric-header">
          <div class="ric-title min-w-0">
            <slot name="name" />
          </div>
          <div class="ric-actions">
            <slot name="actions" />
          </div>
        </div>

        <!-- Conteúdo principal -->
        <div class="ric-body">
          <slot name="content" />
        </div>

        <!-- Footer -->
        <div v-if="$slots.footer" class="ric-footer">
          <slot name="footer" />
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped>
.ric-wrapper {
  border-radius: 8px;
}
.ric-wrapper--selected {
  background: #f1f8e9;
}

.ric-checkbox {
  padding-right: 4px;
}

.ric-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.ric-content {
  padding: 16px; /* Figma: 16 */
  display: flex;
  flex-direction: column;
  gap: 8px; /* Figma: 8 */
}

.ric-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ric-title {
  flex: 1 1 auto;
  min-width: 0;
}

.ric-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ric-body {
  display: flex;
  flex-direction: column;
  gap: 8px; /* mantém o padrão do figma entre linhas */
}

.ric-footer {
  margin-top: 8px;
}
</style>
